import { makeAutoObservable } from 'mobx';
import { SubscriptionModel, Plan, Subscription, PlanName } from '../../model/professional/subscription-model';
import subscriptionStore from '../../store/subscription-store';
import toastStore from '../../store/toast-store';

/**
 * ViewModel pour la gestion des abonnements
 */
class SubscriptionViewModel {
  isLoading: boolean = false;
  error: string | null = null;
  private subscriptionModel: SubscriptionModel;

  constructor() {
    makeAutoObservable(this);
    this.subscriptionModel = SubscriptionModel.getInstance();
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
    subscriptionStore.setIsLoading(isLoading);
  }

  setError(error: string | null) {
    this.error = error;
  }

  /**
   * Récupère tous les plans disponibles
   */
  fetchPlans = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.subscriptionModel.getPlans();

      if (response.success && response.data) {
        subscriptionStore.setAvailablePlans(response.data);
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la récupération des plans';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Récupère l'abonnement actuel
   */
  fetchCurrentSubscription = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.subscriptionModel.getCurrentSubscription();

      if (response.success && response.data) {
        subscriptionStore.setCurrentSubscription(response.data);
        return true;
      } else {
        // Pas d'abonnement n'est pas une erreur
        subscriptionStore.setCurrentSubscription(null);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la récupération de l\'abonnement';
      this.setError(errorMsg);
      subscriptionStore.setCurrentSubscription(null);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Initialise les données (plans + abonnement actuel)
   */
  initialize = async () => {
    await this.fetchPlans();
    await this.fetchCurrentSubscription();
  }

  /**
   * Crée une session de checkout et redirige vers Stripe
   */
  subscribe = async (planId: PlanName) => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.subscriptionModel.createCheckoutSession(planId);

      if (response.success && response.url) {
        window.location.href = response.url;
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la création de la session de paiement';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Change de plan
   */
  changePlan = async (newPlanId: PlanName) => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.subscriptionModel.changePlan(newPlanId);

      if (response.success && response.data) {
        subscriptionStore.setCurrentSubscription(response.data);
        toastStore.success('Plan modifié avec succès');
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors du changement de plan';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Annule l'abonnement
   */
  cancelSubscription = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.subscriptionModel.cancelSubscription();

      if (response.success) {
        await this.fetchCurrentSubscription();
        toastStore.success('Abonnement annulé. Il restera actif jusqu\'à la fin de la période en cours.');
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de l\'annulation de l\'abonnement';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Réactive un abonnement annulé
   */
  reactivateSubscription = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.subscriptionModel.reactivateSubscription();

      if (response.success) {
        await this.fetchCurrentSubscription();
        toastStore.success('Abonnement réactivé avec succès');
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de la réactivation de l\'abonnement';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Ouvre le portail de facturation Stripe
   */
  openBillingPortal = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const response = await this.subscriptionModel.createBillingPortalSession();

      if (response.success && response.url) {
        window.location.href = response.url;
        return true;
      } else {
        this.setError(response.message);
        toastStore.error(response.message);
        return false;
      }
    } catch (error: any) {
      const errorMsg = error?.message || 'Erreur lors de l\'ouverture du portail de facturation';
      this.setError(errorMsg);
      toastStore.error(errorMsg);
      return false;
    } finally {
      this.setLoading(false);
    }
  }


  /**
   * Vérifie si l'utilisateur peut créer une company
   */
  canCreateCompany = async (): Promise<{ canCreate: boolean; currentCount: number; limit: number }> => {
    try {
      const response = await this.subscriptionModel.canCreateCompany();

      if (response.success && response.data) {
        return response.data;
      }

      return { canCreate: false, currentCount: 0, limit: 0 };
    } catch (error) {
      return { canCreate: false, currentCount: 0, limit: 0 };
    }
  }

  /**
   * Vérifie si l'utilisateur peut créer un collaborateur
   */
  canCreateCollaborator = async (): Promise<{ canCreate: boolean; currentCount: number; limit: number }> => {
    try {
      const response = await this.subscriptionModel.canCreateCollaborator();

      if (response.success && response.data) {
        return response.data;
      }

      return { canCreate: false, currentCount: 0, limit: 0 };
    } catch (error) {
      return { canCreate: false, currentCount: 0, limit: 0 };
    }
  }

  /**
   * Retourne les plans triés avec le plan actuel en premier
   */
  get sortedPlans(): Plan[] {
    const plans = subscriptionStore.availablePlans;
    const currentPlanId = subscriptionStore.currentSubscription?.planName;

    if (!currentPlanId) return plans;

    // Mettre le plan actuel en premier
    return [...plans].sort((a, b) => {
      if (a.id === currentPlanId) return -1;
      if (b.id === currentPlanId) return 1;
      return 0;
    });
  }

  /**
   * Vérifie si un plan est un upgrade par rapport au plan actuel
   */
  isUpgrade(planId: PlanName): boolean {
    const currentPlan = subscriptionStore.getCurrentPlan;
    if (!currentPlan) return true;

    const plans: PlanName[] = ['junior', 'premium', 'premium_plus'];
    const currentIndex = plans.indexOf(currentPlan.id);
    const targetIndex = plans.indexOf(planId);

    return targetIndex > currentIndex;
  }

  /**
   * Vérifie si un plan est un downgrade par rapport au plan actuel
   */
  isDowngrade(planId: PlanName): boolean {
    const currentPlan = subscriptionStore.getCurrentPlan;
    if (!currentPlan) return false;

    const plans: PlanName[] = ['junior', 'premium', 'premium_plus'];
    const currentIndex = plans.indexOf(currentPlan.id);
    const targetIndex = plans.indexOf(planId);

    return targetIndex < currentIndex;
  }
}

export const subscriptionViewModel = new SubscriptionViewModel();
